<script setup lang="ts">
import type { StoreBanner } from "~~/shared/types/store";

const props = defineProps<{
  banner: StoreBanner;
}>();

// Inicjujemy od razu wartością z calcRemaining() (działa też na SSR: używa Date),
// żeby blok licznika renderował się JUŻ w SSR HTML. Wcześniej `remaining` był null
// do onMounted → licznik pojawiał się dopiero po hydracji i URASTAŁ baner o linię
// (reflow ~16px), a że baner jest na samej górze, spychał całą stronę = CLS.
// Kompromis: na stronie z edge-cache SSR-owa wartość jest chwilowo nieaktualna
// (z momentu wypełnienia cache), ale klient poprawia ją przy hydracji, a tabular-nums
// trzyma stałą szerokość cyfr → podmiana wartości NIE powoduje przeskoku layoutu.
const remaining = ref<number | null>(calcRemaining());
const closed = ref(false);

function calcRemaining() {
  // Tryb "licznik do północy" (dzienny reset): gdy banner ma flagę `restart`,
  // odliczamy do najbliższej nadchodzącej północy (czas lokalny) zamiast do
  // stałej daty `to`. O północy target sam się przelicza na kolejną dobę,
  // więc licznik nigdy nie osiąga 0 i restartuje się codziennie.
  if (props.banner.restart) {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    return midnight.getTime() - now.getTime();
  }
  if (!props.banner.to) return null;
  const diff = new Date(props.banner.to).getTime() - Date.now();
  return diff > 0 ? diff : 0;
}

function pad(num: number) {
  return String(num).padStart(2, "0");
}

const countdownParts = computed(() => {
  const ms = remaining.value;
  if (ms === null) return null;
  const totalSeconds = Math.floor(ms / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: pad(Math.floor((totalSeconds % 86400) / 3600)),
    minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
    seconds: pad(totalSeconds % 60),
  };
});

const isExpired = computed(() => remaining.value === 0);

const { pause } = useIntervalFn(
  () => {
    const r = calcRemaining();
    remaining.value = r;
    if (r === 0) pause();
  },
  1000,
  { immediateCallback: true },
);

onMounted(() => {
  remaining.value = calcRemaining();
});

const sortedBlocks = computed(() => {
  return [...props.banner.blocks].sort((a, b) => a.position - b.position);
});

const stripStyle = computed(() => {
  const style: Record<string, string> = {};
  if (props.banner.bgColor) style.background = props.banner.bgColor;
  if (props.banner.borderRadius > 0)
    style.borderRadius = `${props.banner.borderRadius}px`;
  if (props.banner.margin) style.margin = props.banner.margin;
  // font jako custom property (NIE inline font-size) — inaczej inline styl wygrałby z mobilną media query i baner nie skompaktowałby się
  if (props.banner.fontSize > 0)
    style["--promo-font-size"] = `${props.banner.fontSize}rem`;
  return style;
});

const digitsColor = computed(() => props.banner.colors?.digits ?? null);
const labelsColor = computed(() => props.banner.colors?.labels ?? null);
const highlightColor = computed(
  () => props.banner.colors?.highlight ?? props.banner.colors?.digits ?? null,
);

const isHighlight = (unit: "days" | "hours" | "minutes" | "seconds") => {
  return props.banner.highlightPosition === unit;
};

function unitStyle(unit: "days" | "hours" | "minutes" | "seconds") {
  const color = isHighlight(unit) ? highlightColor.value : digitsColor.value;
  return color ? { color } : {};
}

const labelStyle = computed(() => {
  return labelsColor.value ? { color: labelsColor.value } : {};
});

const visibilityClass = computed(() => {
  const sizes = props.banner.screenSizes;
  const classes: string[] = [];
  if (!sizes.mobile) classes.push("promo-countdown-banner--hide-mobile");
  if (!sizes.tablet) classes.push("promo-countdown-banner--hide-tablet");
  if (!sizes.desktop) classes.push("promo-countdown-banner--hide-desktop");
  if (!sizes.large) classes.push("promo-countdown-banner--hide-large");
  return classes;
});

function close() {
  closed.value = true;
}
</script>

<template>
  <div
    v-if="!closed && !isExpired"
    class="promo-countdown-banner"
    :class="[
      props.banner.compactView ? 'promo-countdown-banner--compact' : '',
      ...visibilityClass,
    ]"
    :style="stripStyle"
  >
    <div class="promo-countdown-banner__inner">
      <component
        :is="banner.link ? 'a' : 'div'"
        v-for="block in sortedBlocks"
        :key="block.id"
        :href="banner.link || undefined"
        class="promo-countdown-banner__block"
      >
        <template v-if="block.type === 'text' && block.content">
          <!-- eslint-disable-next-line vue/no-v-html -- sanitized by server bannerMapper -->
          <div class="promo-countdown-banner__text" v-html="block.content" />
        </template>
        <template v-else-if="block.type === 'countdown' && countdownParts">
          <div class="promo-countdown-banner__countdown">
            <span
              v-if="countdownParts.days > 0"
              class="promo-countdown-banner__unit"
              :class="{ 'is-highlight': isHighlight('days') }"
            >
              <strong :style="unitStyle('days')">{{
                countdownParts.days
              }}</strong>
              <small :style="labelStyle">{{
                banner.showFullLabels ? "dni" : "d"
              }}</small>
            </span>
            <span
              v-if="banner.showColon && countdownParts.days > 0"
              class="promo-countdown-banner__colon"
              :style="unitStyle('hours')"
              >:</span
            >
            <span
              class="promo-countdown-banner__unit"
              :class="{ 'is-highlight': isHighlight('hours') }"
            >
              <strong :style="unitStyle('hours')">{{
                countdownParts.hours
              }}</strong>
              <small :style="labelStyle">{{
                banner.showFullLabels ? "godz." : "h"
              }}</small>
            </span>
            <span
              v-if="banner.showColon"
              class="promo-countdown-banner__colon"
              :style="unitStyle('minutes')"
              >:</span
            >
            <span
              class="promo-countdown-banner__unit"
              :class="{ 'is-highlight': isHighlight('minutes') }"
            >
              <strong :style="unitStyle('minutes')">{{
                countdownParts.minutes
              }}</strong>
              <small :style="labelStyle">{{
                banner.showFullLabels ? "min." : "m"
              }}</small>
            </span>
            <span
              v-if="banner.showColon"
              class="promo-countdown-banner__colon"
              :style="unitStyle('seconds')"
              >:</span
            >
            <span
              class="promo-countdown-banner__unit"
              :class="{ 'is-highlight': isHighlight('seconds') }"
            >
              <strong :style="unitStyle('seconds')">{{
                countdownParts.seconds
              }}</strong>
              <small :style="labelStyle">{{
                banner.showFullLabels ? "sek." : "s"
              }}</small>
            </span>
          </div>
        </template>
        <template v-else-if="block.type === 'image' && block.image">
          <img
            :src="block.image"
            :alt="banner.systemName"
            class="promo-countdown-banner__image"
            loading="lazy"
          />
        </template>
      </component>
    </div>

    <button
      v-if="banner.closeBtn"
      type="button"
      class="promo-countdown-banner__close"
      aria-label="Zamknij baner promocyjny"
      @click="close"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.promo-countdown-banner {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  background: var(--color-promo-bg, #0a0a0a);
  color: var(--color-promo-text, #fff);
  font-size: var(--promo-font-size, 0.875rem);
  line-height: 1.3;
  box-sizing: border-box;
}

.promo-countdown-banner--compact {
  padding: 6px 16px;
}

.promo-countdown-banner__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  max-width: 1200px;
}

.promo-countdown-banner__block {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
}

.promo-countdown-banner__text :deep(p) {
  margin: 0;
  text-align: center;
}

.promo-countdown-banner__text :deep(strong) {
  font-weight: 700;
}

.promo-countdown-banner__countdown {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-variant-numeric: tabular-nums;
}

.promo-countdown-banner__unit {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  font-weight: 700;
}

.promo-countdown-banner__unit strong {
  font-size: 1em;
  font-weight: 700;
}

.promo-countdown-banner__unit small {
  font-size: 0.75em;
  font-weight: 400;
  opacity: 0.8;
}

.promo-countdown-banner__colon {
  font-weight: 700;
  opacity: 0.6;
}

.promo-countdown-banner__image {
  display: block;
  max-height: 32px;
  width: auto;
}

.promo-countdown-banner__close {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 0;
  padding: 6px;
  cursor: pointer;
  color: inherit;
  opacity: 0.65;
  line-height: 0;
}

.promo-countdown-banner__close:hover {
  opacity: 1;
}

@media (max-width: 575.98px) {
  .promo-countdown-banner--hide-mobile {
    display: none;
  }

  /* Kompaktacja na mobile — baner zabierał za dużo wysokości (tekst łamał się na 2 linie + licznik w 3.) */
  .promo-countdown-banner {
    padding: 6px 12px;
    font-size: 0.8rem;
    line-height: 1.25;
  }

  .promo-countdown-banner--compact {
    padding: 4px 12px;
  }

  .promo-countdown-banner__inner {
    gap: 6px 10px;
  }

  .promo-countdown-banner__countdown {
    gap: 3px;
  }
}

@media (min-width: 576px) and (max-width: 991.98px) {
  .promo-countdown-banner--hide-tablet {
    display: none;
  }
}

@media (min-width: 992px) and (max-width: 1199.98px) {
  .promo-countdown-banner--hide-desktop {
    display: none;
  }
}

@media (min-width: 1200px) {
  .promo-countdown-banner--hide-large {
    display: none;
  }
}
</style>
